import {
  Box,
  Button,
  Divider,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { Fragment, useMemo } from 'react'
import { IconButton, Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import {
  ItemResaleRaffleUserDetailFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ResaleItem as OResaleItem } from '@/components/organisms'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  openError: boolean
  submitting: boolean
  itemResaleRaffleUser: Maybe<ItemResaleRaffleUserDetailFragment> | undefined
  brand: string
  cardNumber?: string
  openExpiredCardModal: boolean
  openPaymentErrorModal: boolean
  openRequire3DSecureModal: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onPurchase: () => Promise<void>
  onCloseError: () => void
  onCloseRequire3DSecureModal: () => void
  onCloseExpiredCardModal: () => void
  onClosePaymentErrorModal: () => void
}

export const ResaleRaffleRepaymentConfirmationTemplate: Template<
  State,
  Action
> = ({
  state,
  onPurchase,
  onCloseError,
  onCloseRequire3DSecureModal,
  onCloseExpiredCardModal,
  onClosePaymentErrorModal,
}) => {
  const { t } = useTranslation('resaleRaffleRepaymentConfirmation')
  const { routerPages, router } = useRouter()
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resalePayment'))

  return (
    <BaseLayout
      margin
      header={{
        back: true,
        title: t('title'),
        right: (
          <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
        ),
      }}
    >
      {state.loading ? (
        <ResaleRaffleRepaymentConfirmationSkeleton />
      ) : (
        <>
          <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
            <Button
              size="large"
              sx={{ width: 212 }}
              disabled={state.loading || !state.cardNumber}
              loading={state.submitting}
              onClick={onPurchase}
            >
              {t('submit')}
            </Button>
          </Stack>
          <Typography variant="h4" sx={{ py: 5 }}>
            {t('resaleInfo')}
          </Typography>
          <ConfirmationContentCell
            itemResaleRaffleUser={state.itemResaleRaffleUser}
            cardNumber={state.cardNumber}
            brand={state.brand}
          />
          <Stack spacing={3} alignItems="center" sx={{ pb: 6 }}>
            <Button
              size="large"
              sx={{ width: 212 }}
              disabled={state.loading || !state.cardNumber}
              loading={state.submitting}
              onClick={onPurchase}
            >
              {t('submit')}
            </Button>
            <Button
              variant="text"
              sx={{ width: 212 }}
              onClick={() => router.goBack()}
            >
              {t('back')}
            </Button>
          </Stack>
          <InformationModal
            open={state.openError}
            onClose={onCloseError}
            title={
              <Stack alignItems="center" spacing={4}>
                <Icons.Error fontSize="large" sx={{ color: 'error.main' }} />
                <Typography variant="h5" sx={{ pb: 3 }}>
                  {t('errorTitle')}
                </Typography>
              </Stack>
            }
            content={t('errorDesc')}
            buttons={
              <Button
                size="large"
                sx={{ width: 212 }}
                href={routerPages.PaymentDetail}
              >
                {t('changeRafflePaymentMethod')}
              </Button>
            }
            cancelText={t('close')}
          />
        </>
      )}
      <Require3DSecureModal
        open={state.openRequire3DSecureModal}
        onClose={onCloseRequire3DSecureModal}
      />

      <ExpiredCardModal
        open={state.openExpiredCardModal}
        onClose={onCloseExpiredCardModal}
      />

      <PaymentErrorModal
        open={state.openPaymentErrorModal}
        onClose={onClosePaymentErrorModal}
      />
    </BaseLayout>
  )
}

const ResaleRaffleRepaymentConfirmationSkeleton: Cell = () => (
  <CellFrame>
    <Stack alignItems="center" sx={{ py: 4 }}>
      <Skeleton
        variant="rectangular"
        height={44}
        width={212}
        sx={{ borderRadius: 34 }}
      />
    </Stack>
    <Box sx={{ py: 5 }}>
      <SkeletonText variant="h4" width="50%" />
    </Box>
    <Box sx={{ pt: 5, pb: 2 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      {[0, 1, 2].map(key => (
        <Stack spacing={1} sx={{ pt: 3 }} key={key}>
          <SkeletonText variant="body2" width="10%" />
          <SkeletonText variant="body2" width="90%" />
          <Divider sx={{ pt: 3 }} />
        </Stack>
      ))}
    </Box>
    <Stack spacing={2} sx={{ pt: 5 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <Skeleton
        variant="rectangular"
        height={20}
        width="100%"
        sx={{ bgcolor: 'grey.100', my: '6px' }}
      />
      <Divider sx={{ pt: 3 }} />
    </Stack>
    <Stack sx={{ pt: 5 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <Box>
        <SkeletonText variant="body2" numberOfLine={2} />
      </Box>
    </Stack>
    <Stack spacing={2} sx={{ pt: '34px' }}>
      {[0, 1, 2].map(key => (
        <Fragment key={key}>
          <Stack direction="row" justifyContent="space-between">
            <SkeletonText variant="small" width="30%" />
            <SkeletonText variant="small" width="30%" />
          </Stack>
          {key < 2 && <Divider />}
        </Fragment>
      ))}
      <Stack alignItems="center" sx={{ pt: 8, pb: 6 }}>
        <Skeleton
          variant="rectangular"
          height={44}
          width={212}
          sx={{ borderRadius: 34 }}
        />
        <SkeletonText variant="caption" width="15%" sx={{ mt: 2 }} />
      </Stack>
    </Stack>
  </CellFrame>
)

type ConfirmationContentCellProps = {
  itemResaleRaffleUser: State['itemResaleRaffleUser']
  brand: State['brand']
  cardNumber: State['cardNumber']
}
const ConfirmationContentCell: Cell<ConfirmationContentCellProps> = ({
  itemResaleRaffleUser,
  brand,
  cardNumber,
}) => {
  const { t } = useTranslation('resaleRaffleRepaymentConfirmation')
  const { routerPages } = useRouter()
  const { price, formattedPrice } = useMemo(
    () => getItemSkuPrice(itemResaleRaffleUser?.item),
    [itemResaleRaffleUser?.item]
  )

  const totalAmount = useMemo(() => {
    if (!price) {
      return 0
    }
    return numeral(
      (itemResaleRaffleUser?.totalItemPrice ?? 0) +
        (itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee?.purchaseFee ||
          0)
    ).format('0,0')
  }, [
    price,
    itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee?.purchaseFee,
    itemResaleRaffleUser?.totalItemPrice,
  ])

  const data = [
    {
      label: t('ticketPrice'),
      value: `￥${numeral(
        price * (itemResaleRaffleUser?.itemCount || 0)
      ).format('0,0')}`,
    },
    {
      label: t('transferFee'),
      value: `¥${numeral(
        itemResaleRaffleUser?.item.metadata?.ticket?.resaleFee?.purchaseFee || 0
      ).format('0,0')}`,
    },
  ]

  return (
    <CellFrame>
      <OResaleItem title={t('applicationInfo')}>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('ticketName')}</Typography>
            <Typography variant="body2">
              {itemResaleRaffleUser?.item?.title}
            </Typography>
            {!!itemResaleRaffleUser?.item?.sku?.unitName && (
              <Typography variant="subtitle1">
                {itemResaleRaffleUser.item.sku.unitName}
              </Typography>
            )}
          </Stack>
          <Divider />
          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('ticketPriceLabel')}</Typography>
            <Typography variant="body2">
              {t('ticketPriceValue', { price: formattedPrice })}
            </Typography>
          </Stack>
          <Divider />
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {t('applicationNumber')}
            </Typography>
            <Typography variant="body2">
              {itemResaleRaffleUser?.itemCount || 0}
              {t('sheet')}
            </Typography>
          </Stack>
        </Stack>
      </OResaleItem>
      <OResaleItem title={t('paymentInfo')}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1" color="text.secondary">
            {t('creditCardInfo')}
          </Typography>
          {!cardNumber && (
            <Link variant="subtitle1" href={routerPages.PaymentMethodSetting}>
              {t('register')}
            </Link>
          )}
        </Stack>
        {!cardNumber ? (
          <Typography variant="caption" color="text.hint">
            {t('cardInfoNotRegister')}
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={3}>
            <StorageImage
              width={40}
              height={32}
              src={generateCreditCardLogoPath(brand)}
              target="common"
            />
            <Typography variant="body2">
              {t('last4Digit', { number: cardNumber })}
            </Typography>
          </Stack>
        )}
      </OResaleItem>
      <OResaleItem title={t('totalRafflePayment')}>
        <Stack spacing={3}>
          <Stack>
            {data.map(({ value, label }, index) => (
              <Fragment key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Stack>
                <Divider />
              </Fragment>
            ))}
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ py: 2 }}
            >
              <Typography variant="subtitle1" color="text.secondary">
                {t('total')}
              </Typography>
              <Typography variant="h6">¥{totalAmount}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </OResaleItem>
    </CellFrame>
  )
}
